import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Bell, BellOff } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

interface CardRow {
  id: string;
  nickname: string | null;
  bank: string | null;
  lastFour: string | null;
}
interface BankRow {
  id: string;
  nickname: string | null;
  bank: string | null;
  accountNumberMasked: string | null;
  otpOwnerUserId: string | null;
}
interface Watch {
  id: string;
  cardId: string | null;
  bankAccountId: string | null;
}

/**
 * OTP Alerts — each user ticks the cards / bank accounts they want a
 * desktop notification for. When an OTP arrives for a ticked instrument
 * it pops as a browser notification (Mac or Windows). Per-user: every
 * admin manages their own list.
 */
export function OtpAlertsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Only the user's OWN instruments — watching a teammate's card requires
  // a session, so it must not appear here as a tickable option.
  const cards = useQuery<CardRow[]>({
    queryKey: ['cards', 'mine'],
    queryFn: () => api('/api/cards?ownedByMe=true'),
  });
  const banks = useQuery<BankRow[]>({ queryKey: ['bank-accounts'], queryFn: () => api('/api/bank-accounts') });
  const watches = useQuery<Watch[]>({ queryKey: ['otp-watches'], queryFn: () => api('/api/otp-watches') });

  const [perm, setPerm] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied',
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  const watchByCard = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of watches.data ?? []) if (w.cardId) m.set(w.cardId, w.id);
    return m;
  }, [watches.data]);
  const watchByBank = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of watches.data ?? []) if (w.bankAccountId) m.set(w.bankAccountId, w.id);
    return m;
  }, [watches.data]);

  const toggle = async (
    key: string,
    watchId: string | undefined,
    body: { cardId: string } | { bankAccountId: string },
  ) => {
    setBusyId(key);
    try {
      if (watchId) {
        await api(`/api/otp-watches/${watchId}`, { method: 'DELETE' });
      } else {
        await api('/api/otp-watches', { method: 'POST', body });
      }
      await qc.invalidateQueries({ queryKey: ['otp-watches'] });
    } finally {
      setBusyId(null);
    }
  };

  const askPermission = () => {
    if (typeof Notification === 'undefined') return;
    void Notification.requestPermission().then(setPerm);
  };

  const cardList = (cards.data ?? []).slice().sort((a, b) =>
    (a.nickname ?? '').localeCompare(b.nickname ?? ''),
  );
  const bankList = (banks.data ?? [])
    .filter((b) => b.otpOwnerUserId === user?.id)
    .slice()
    .sort((a, b) => (a.nickname ?? '').localeCompare(b.nickname ?? ''));
  const loading = cards.isLoading || banks.isLoading || watches.isLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">OTP Alerts</h1>
        <p className="text-sm text-muted-foreground">
          Tick the cards and accounts you want an OTP desktop notification for. Only your own
          instruments are listed — to receive a teammate's OTPs, raise a session. Works on this
          browser (Mac or Windows); each person manages their own list.
        </p>
      </div>

      {perm !== 'granted' ? (
        <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <BellOff className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            Browser notifications are {perm === 'denied' ? 'blocked' : 'off'} — alerts won't show
            until you {perm === 'denied' ? 'allow them in browser settings' : 'enable them'}.
          </span>
          {perm !== 'denied' ? (
            <button
              type="button"
              onClick={askPermission}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
            >
              Enable
            </button>
          ) : null}
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
          <Bell className="h-4 w-4" />
          Notifications are on.
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cards</CardTitle>
              <CardDescription>OTP notifications for company cards.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {cardList.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">No cards.</p>
              ) : (
                <ul className="divide-y">
                  {cardList.map((c) => {
                    const watchId = watchByCard.get(c.id);
                    const label = c.nickname ?? [c.bank, c.lastFour].filter(Boolean).join(' ');
                    return (
                      <li key={c.id} className="flex items-center justify-between px-4 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{label}</p>
                          {c.bank ? (
                            <p className="truncate text-xs text-muted-foreground">
                              {c.bank} {c.lastFour ? `••${c.lastFour}` : ''}
                            </p>
                          ) : null}
                        </div>
                        <input
                          type="checkbox"
                          checked={!!watchId}
                          disabled={busyId === c.id}
                          onChange={() => void toggle(c.id, watchId, { cardId: c.id })}
                          className="h-4 w-4 cursor-pointer accent-blue-600"
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Bank accounts</CardTitle>
              <CardDescription>OTP notifications for company bank accounts.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {bankList.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No bank accounts.
                </p>
              ) : (
                <ul className="divide-y">
                  {bankList.map((b) => {
                    const watchId = watchByBank.get(b.id);
                    const label = b.nickname ?? b.bank ?? 'Account';
                    return (
                      <li key={b.id} className="flex items-center justify-between px-4 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{label}</p>
                          {b.accountNumberMasked ? (
                            <p className="truncate text-xs text-muted-foreground">
                              {b.bank} {b.accountNumberMasked}
                            </p>
                          ) : null}
                        </div>
                        <input
                          type="checkbox"
                          checked={!!watchId}
                          disabled={busyId === b.id}
                          onChange={() => void toggle(b.id, watchId, { bankAccountId: b.id })}
                          className="h-4 w-4 cursor-pointer accent-blue-600"
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
